/**
 * Définition des routes (React Router 6) et gardes d'authentification.
 *
 * Les écrans sont chargés à la demande (`React.lazy`) : chaque page devient un morceau
 * distinct, ce qui évite de charger l'ensemble de l'interface sur l'écran de connexion.
 */

import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material';
import { WEB_ROUTES, type PermissionCode } from '@noryvox/shared';
import { Suspense, lazy, type ReactElement } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';

import { SESSION_EXPIRED_REASON, useAuth } from './auth/session';

const ConnexionPage = lazy(() => import('./pages/connexion/ConnexionPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const InvitationPage = lazy(() => import('./pages/invitation/InvitationPage'));
const InscriptionPage = lazy(() => import('./pages/inscription/InscriptionPage'));
const PublicLegalPage = lazy(() => import('./pages/public/PublicLegalPage'));
const ActivitesPage = lazy(() => import('./pages/activites/ActivitesPage'));
const AgendaPage = lazy(() => import('./pages/agenda/AgendaPage'));
const PipelinePage = lazy(() => import('./pages/pipeline/PipelinePage'));
const RolesPage = lazy(() => import('./pages/roles/RolesPage'));
const SecuritePage = lazy(() => import('./pages/securite/SecuritePage'));
const TachesPage = lazy(() => import('./pages/taches/TachesPage'));
const TiersPage = lazy(() => import('./pages/tiers/TiersPage'));
const DevisPage = lazy(() => import('./pages/ventes/DevisPage'));
const UtilisateursPage = lazy(() => import('./pages/utilisateurs/UtilisateursPage'));
const ArticlesPage = lazy(() => import('./pages/catalogue/ArticlesPage'));
const CategoriesPage = lazy(() => import('./pages/catalogue/CategoriesPage'));
const SuppliersPage = lazy(() => import('./pages/catalogue/SuppliersPage'));
const ParametresPage = lazy(() => import('./pages/catalogue/ParametresPage'));
const TextBlocksPage = lazy(() => import('./pages/catalogue/TextBlocksPage'));
const SocietePage = lazy(() => import('./pages/societe/SocietePage'));
const ParametresEmailPage = lazy(() => import('./pages/parametres/ParametresEmailPage'));

/** Chemins des écrans activités et tâches (troisième incrément CRM). */
export const ACTIVITES_ROUTE = '/crm/activites';
export const TACHES_ROUTE = '/crm/taches';

/** Écran agenda : rendez-vous et tâches planifiées (épopée E26). */
export const AGENDA_ROUTE = '/crm/agenda';

/** Écran de chargement affiché pendant la résolution de la session initiale. */
function FullPageLoader(): ReactElement {
  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
      role="status"
      aria-live="polite"
    >
      <CircularProgress aria-label="Chargement de la session" />
    </Box>
  );
}

/** Route protégée : redirige vers la connexion si la session n'est pas établie. */
export function PrivateRoute({ children }: { children: ReactElement }): ReactElement {
  const { status } = useAuth();

  if (status === 'loading') {
    return <FullPageLoader />;
  }
  if (status !== 'authenticated') {
    return <Navigate to={WEB_ROUTES.login} replace state={{ reason: SESSION_EXPIRED_REASON }} />;
  }
  return children;
}

/** Route publique : renvoie un utilisateur déjà connecté vers le tableau de bord. */
export function PublicOnlyRoute({ children }: { children: ReactElement }): ReactElement {
  const { status } = useAuth();

  if (status === 'loading') {
    return <FullPageLoader />;
  }
  if (status === 'authenticated') {
    return <Navigate to={WEB_ROUTES.dashboard} replace />;
  }
  return children;
}

/** Écran affiché lorsqu'une permission requise fait défaut (déni par défaut). */
function AccessDeniedPage(): ReactElement {
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Alert severity="warning">
        <Typography variant="h6" gutterBottom>
          Accès refusé
        </Typography>
        <Typography variant="body2">
          Votre rôle ne dispose pas des droits nécessaires pour consulter cette page.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          <Link to={WEB_ROUTES.dashboard}>Retour au tableau de bord</Link>
        </Typography>
      </Alert>
    </Container>
  );
}

/**
 * Garde de permission : affiche l'écran de refus si le droit est absent.
 * Le contrôle réel reste assuré par l'API ; cette garde évite une navigation sans issue.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionCode;
  children: ReactElement;
}): ReactElement {
  const { can } = useAuth();
  return can(permission) ? children : <AccessDeniedPage />;
}

function RequireAnyPermission({
  permissions,
  children,
}: {
  permissions: readonly PermissionCode[];
  children: ReactElement;
}): ReactElement {
  const { can } = useAuth();
  return permissions.some((permission) => can(permission)) ? children : <AccessDeniedPage />;
}

export function AppRoutes(): ReactElement {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path={WEB_ROUTES.invitation} element={<InvitationPage />} />
        <Route
          path={WEB_ROUTES.companyPrivacy}
          element={<PublicLegalPage documentKind="privacy" />}
        />
        <Route path={WEB_ROUTES.companyTerms} element={<PublicLegalPage documentKind="terms" />} />
        <Route
          path={WEB_ROUTES.login}
          element={
            <PublicOnlyRoute>
              <ConnexionPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path={WEB_ROUTES.register}
          element={
            <PublicOnlyRoute>
              <InscriptionPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path={WEB_ROUTES.dashboard}
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.tiers}
          element={
            <PrivateRoute>
              <RequireAnyPermission permissions={['prospect.read', 'client.read']}>
                <TiersPage />
              </RequireAnyPermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.clients}
          element={
            <PrivateRoute>
              <RequireAnyPermission permissions={['prospect.read', 'client.read']}>
                <Navigate to={`${WEB_ROUTES.tiers}?scope=clients`} replace />
              </RequireAnyPermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.prospects}
          element={
            <PrivateRoute>
              <RequireAnyPermission permissions={['prospect.read', 'client.read']}>
                <Navigate to={`${WEB_ROUTES.tiers}?scope=prospects`} replace />
              </RequireAnyPermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.pipeline}
          element={
            <PrivateRoute>
              <RequirePermission permission="opportunity.read">
                <PipelinePage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={ACTIVITES_ROUTE}
          element={
            <PrivateRoute>
              <RequirePermission permission="activity.read">
                <ActivitesPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={TACHES_ROUTE}
          element={
            <PrivateRoute>
              <RequirePermission permission="task.read">
                <TachesPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={AGENDA_ROUTE}
          element={
            <PrivateRoute>
              <RequirePermission permission="calendar.read">
                <AgendaPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.quotes}
          element={
            <PrivateRoute>
              <RequirePermission permission="quote.read">
                <DevisPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.catalogueItems}
          element={
            <PrivateRoute>
              <RequirePermission permission="item.read">
                <ArticlesPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.catalogueCategories}
          element={
            <PrivateRoute>
              <RequirePermission permission="item.read">
                <CategoriesPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.catalogueSuppliers}
          element={
            <PrivateRoute>
              <RequirePermission permission="supplier.read">
                <SuppliersPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.catalogueTextBlocks}
          element={
            <PrivateRoute>
              <RequirePermission permission="item.read">
                <TextBlocksPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.catalogueSettings}
          element={
            <PrivateRoute>
              <RequirePermission permission="item.read">
                <ParametresPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.settingsSecurity}
          element={
            <PrivateRoute>
              <SecuritePage />
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.settingsCompany}
          element={
            <PrivateRoute>
              <RequirePermission permission="settings.read">
                <SocietePage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.settingsEmail}
          element={
            <PrivateRoute>
              <RequirePermission permission="settings.read">
                <ParametresEmailPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.settingsRoles}
          element={
            <PrivateRoute>
              <RequirePermission permission="admin.read">
                <RolesPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route
          path={WEB_ROUTES.settingsUsers}
          element={
            <PrivateRoute>
              <RequirePermission permission="admin.read">
                <UtilisateursPage />
              </RequirePermission>
            </PrivateRoute>
          }
        />
        <Route path={WEB_ROUTES.home} element={<Navigate to={WEB_ROUTES.dashboard} replace />} />
        <Route path="*" element={<Navigate to={WEB_ROUTES.dashboard} replace />} />
      </Routes>
    </Suspense>
  );
}
